import React, { useEffect, useState, type CSSProperties } from 'react';
import {
  answerClarification,
  confirmWorkRequestResult,
  getWorkRequests,
} from '../api';
import type { PassportTab } from './RequestPassportModal';
import { RequestPassportModal } from './RequestPassportModal';
import {
  CreateRequestEntry,
  canOpenRequestGateway,
  gatewayRoleLabel,
} from '@/features/request-gateway';
import type { WorkRequestKpis, WorkRequestListItem } from '../types';

interface RoleMyRequestsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  roleContext?: string;
  onOpenPreviousRequests?: () => void;
}

function statusAccent(status: string): string {
  if (status === 'IN_PROGRESS') return '#41d2ff';
  if (status === 'NEEDS_CLARIFICATION') return '#e2ad3a';
  if (status === 'COMPLETED') return '#49ddb7';
  return '#8faebb';
}

/** Role-scoped «Мои заявки» — owner-uat overlay chrome. */
export const RoleMyRequestsOverlay: React.FC<RoleMyRequestsOverlayProps> = ({
  isOpen,
  onClose,
  roleContext,
  onOpenPreviousRequests,
}) => {
  const [requests, setRequests] = useState<WorkRequestListItem[]>([]);
  const [counts, setCounts] = useState<WorkRequestKpis>({
    total: 0,
    in_progress: 0,
    needs_clarification: 0,
    overdue: 0,
    not_started: 0,
    completed: 0,
    draft: 0,
    cancelled: 0,
  });
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [passportInitialTab, setPassportInitialTab] = useState<PassportTab>('overview');
  const [isPassportOpen, setIsPassportOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [quickAnswerReqId, setQuickAnswerReqId] = useState<string | null>(null);
  const [quickAnswerText, setQuickAnswerText] = useState('');
  const [answeringLoading, setAnsweringLoading] = useState(false);

  const fetchMyRequests = async () => {
    try {
      setLoading(true);
      const res = await getWorkRequests({
        filter: activeFilter,
        search: search.trim() || undefined,
        role_context: roleContext,
      });
      setRequests(res.items || []);
      setCounts(res.counts);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) void fetchMyRequests();
  }, [isOpen, activeFilter, search, roleContext]);

  if (!isOpen) return null;

  const canCreate = canOpenRequestGateway(roleContext);

  const handleOpenPassport = (id: string, tab: PassportTab = 'overview') => {
    setSelectedRequestId(id);
    setPassportInitialTab(tab);
    setIsPassportOpen(true);
  };

  const handleQuickAnswer = async (req: WorkRequestListItem) => {
    if (!quickAnswerText.trim()) return;
    try {
      setAnsweringLoading(true);
      await answerClarification(
        req.id,
        'first',
        { answer: quickAnswerText.trim() },
        roleContext,
      );
      setQuickAnswerReqId(null);
      setQuickAnswerText('');
      await fetchMyRequests();
    } catch {
      /* keep form */
    } finally {
      setAnsweringLoading(false);
    }
  };

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Мои заявки</small>
              <h2>Мои заявки в работе</h2>
              <p>
                Контроль выполнения услуг, ответы на уточнения и приёмка результатов
              </p>
            </div>
          </div>
          <div className="cab-top-actions">
            {onOpenPreviousRequests && <button type="button" className="cab-btn" onClick={onOpenPreviousRequests}>Прежние заявки</button>}
            {canCreate ? (
              <button type="button" className="cab-btn primary" onClick={() => setIsCreateOpen(true)}>
                + Новая заявка
              </button>
            ) : null}
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div className="owner-uat-toolbar" style={{ padding: '12px 18px', flexShrink: 0 }}>
          <div className="owner-uat-filters">
            {[
              ['ALL', `Все · ${counts.total}`],
              ['IN_PROGRESS', `В работе · ${counts.in_progress}`],
              ['NEEDS_CLARIFICATION', `Требуют ответа · ${counts.needs_clarification}`],
              ['COMPLETED', `Завершённые · ${counts.completed}`],
            ].map(([code, label]) => (
              <button
                key={code}
                type="button"
                className={`owner-uat-filter${activeFilter === code ? ' active' : ''}`}
                onClick={() => setActiveFilter(code)}
              >
                {label}
              </button>
            ))}
          </div>
          <input
            className="owner-uat-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по моим заявкам…"
          />
        </div>

        <div className="cab-body" style={{ padding: 18 }}>
          {loading ? <div className="owner-uat-note">Загрузка ваших заявок…</div> : null}

          {!loading && requests.length === 0 ? (
            <div className="owner-uat-note">
              У вас пока нет заявок в этом статусе. Можно подать новую заявку кнопкой выше.
            </div>
          ) : null}

          {!loading && requests.length > 0 ? (
            <div className="owner-uat-request-list owner-uat-request-list--stack">
              {requests.map((r) => (
                <article key={r.id} className="owner-uat-request-card owner-uat-request-card--stack">
                  <header className="owner-uat-req-head">
                    <div className="owner-uat-req-badges">
                      <span
                        className="owner-uat-badge"
                        style={{ '--badge': '#e2ad3a' } as CSSProperties}
                      >
                        {r.reference_code}
                      </span>
                      <span
                        className="owner-uat-badge"
                        style={{ '--badge': statusAccent(r.status) } as CSSProperties}
                      >
                        {r.status_label}
                      </span>
                    </div>
                    <div className="owner-uat-req-meta">
                      {r.due_at_label ? <span>Срок: {r.due_at_label}</span> : null}
                      {r.stage ? <span>Этап: {r.stage}</span> : null}
                    </div>
                  </header>

                  <div className="owner-uat-req-main">
                    <strong>{r.title || r.service_name}</strong>
                    <p>
                      {r.service_name}
                      {r.direction ? ` · ${r.direction}` : ''}
                      {r.assignee ? ` · Исполнитель: ${r.assignee}` : ''}
                    </p>
                  </div>

                  {r.pending_clarification ? (
                    <div className="owner-uat-note warn owner-uat-req-alert">
                      <div className="owner-uat-req-alert-copy">
                        <strong>Требуется ваш ответ на уточнение координатора</strong>
                        <button
                          type="button"
                          className="owner-uat-btn"
                          onClick={() => handleOpenPassport(r.id, 'clarifications')}
                        >
                          К уточнениям →
                        </button>
                      </div>
                      {quickAnswerReqId === r.id ? (
                        <div className="owner-uat-req-quick">
                          <textarea
                            className="owner-uat-textarea"
                            rows={2}
                            value={quickAnswerText}
                            onChange={(e) => setQuickAnswerText(e.target.value)}
                            placeholder="Введите ваш ответ…"
                          />
                          <div className="owner-uat-actions">
                            <button
                              type="button"
                              className="owner-uat-btn"
                              onClick={() => setQuickAnswerReqId(null)}
                            >
                              Отмена
                            </button>
                            <button
                              type="button"
                              className="owner-uat-btn primary"
                              disabled={answeringLoading || !quickAnswerText.trim()}
                              onClick={() => void handleQuickAnswer(r)}
                            >
                              {answeringLoading ? 'Отправка…' : 'Отправить ответ'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="owner-uat-btn primary"
                          onClick={() => setQuickAnswerReqId(r.id)}
                        >
                          Ответить быстро
                        </button>
                      )}
                    </div>
                  ) : null}

                  <footer className="owner-uat-req-actions">
                    <button
                      type="button"
                      className="owner-uat-btn"
                      onClick={() => handleOpenPassport(r.id, 'overview')}
                    >
                      Паспорт заявки
                    </button>
                    <button
                      type="button"
                      className="owner-uat-btn"
                      onClick={() => handleOpenPassport(r.id, 'answers')}
                    >
                      Материалы и ответы
                    </button>
                    {r.status === 'COMPLETED' ? (
                      <button
                        type="button"
                        className="owner-uat-btn primary"
                        onClick={() =>
                          void confirmWorkRequestResult(r.id, roleContext).then(fetchMyRequests)
                        }
                      >
                        Принять результат
                      </button>
                    ) : null}
                  </footer>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <RequestPassportModal
        isOpen={isPassportOpen}
        requestIdOrCode={selectedRequestId}
        initialTab={passportInitialTab}
        onClose={() => setIsPassportOpen(false)}
        onUpdated={() => void fetchMyRequests()}
        roleContext={roleContext}
      />

      <CreateRequestEntry
        isOpen={isCreateOpen}
        onClose={() => {
          setIsCreateOpen(false);
          void fetchMyRequests();
        }}
        activeRole={roleContext || 'SYSTEM_OWNER'}
        roleLabel={gatewayRoleLabel(roleContext)}
      />
    </div>
  );
};
